import { testAiConnection } from "../ai/testConnection";
import { getEffectiveLevel, getSettings, getUserLevelProfile, saveSettings, setManualLevelOverride } from "../db";
import { LEVEL_LABELS } from "../level";
import type { EnglishLevel, Route } from "../types";
import { getViewEl } from "../ui/layout";
import { esc, toast } from "../ui/toast";

const AGNES_PRESET = {
  ollamaApiUrl: "https://apihub.agnes-ai.com/v1",
  ollamaModel: "agnes-2.0-flash",
};

function formatAssessedAt(iso: string | null): string {
  if (!iso) {
    return "尚未评估";
  }
  return iso.slice(0, 19).replace("T", " ");
}

export async function renderSettingsPage(_route: Route): Promise<void> {
  const view = getViewEl();
  if (!view) {
    return;
  }

  const [settings, profile, effectiveLevel] = await Promise.all([
    getSettings(),
    getUserLevelProfile(),
    getEffectiveLevel(),
  ]);
  const hasKey = Boolean(settings.apiKey);
  const useAutoAssessment = profile.manualOverride === null;

  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>设置</h1>
        <p class="lead">默认英语水平与 AI 连接。接口由浏览器直接请求，请确保本机或局域网可访问。</p>
      </div>
    </div>

    <section class="panel settings-panel">
      <h2>你的评估水平</h2>
      <div class="level-assessment-card">
        <p class="level-assessment-main">
          <span class="badge badge-level">${esc(LEVEL_LABELS[profile.assessedLevel])}</span>
          <span class="muted small">（自动评估）</span>
        </p>
        ${profile.levelReason ? `<p class="muted small">${esc(profile.levelReason)}</p>` : ""}
        <p class="muted small">上次更新：${esc(formatAssessedAt(profile.lastAssessedAt))} · 置信度 ${Math.round(profile.confidence * 100)}%</p>
        <p class="muted small">当前生效水平：${esc(LEVEL_LABELS[effectiveLevel])}</p>
        <label class="field field-checkbox">
          <input type="checkbox" id="useAutoAssessment" ${useAutoAssessment ? "checked" : ""} />
          <span>使用自动评估</span>
        </label>
        <label class="field" id="manualLevelField" ${useAutoAssessment ? 'style="display:none"' : ""}>
          <span>手动固定水平</span>
          <select id="manualLevel">
            <option value="beginner">初级</option>
            <option value="intermediate">中级</option>
            <option value="advanced">高级</option>
          </select>
        </label>
      </div>
    </section>

    <section class="panel settings-panel">
      <label class="field">
        <span>默认英语水平（无评估时的回退）</span>
        <select id="settingLevel">
          <option value="beginner">初级</option>
          <option value="intermediate">中级</option>
          <option value="advanced">高级</option>
        </select>
      </label>
      <label class="field">
        <span>AI 接口地址</span>
        <input id="settingApiUrl" type="url" placeholder="http://127.0.0.1:11434" />
      </label>
      <label class="field">
        <span>AI 模型</span>
        <input id="settingModel" type="text" placeholder="gemma3:latest" />
      </label>
      <label class="field">
        <span>API Key（可选，Agnes 等云端服务需要）</span>
        <input id="settingApiKey" type="password" autocomplete="off" placeholder="${hasKey ? "已设置，留空则不修改" : "sk-..."}" />
        <p class="muted small">Key 仅保存在本机浏览器，不会写入 Excel 备份。</p>
      </label>
      <div class="actions">
        <button type="button" class="btn" id="btnAgnesPreset">使用 Agnes 预设</button>
        <button type="button" class="btn" id="btnTestConnection">测试连通性</button>
        <button type="button" class="btn btn-primary" id="btnSaveSettings">保存设置</button>
      </div>
    </section>
  `;

  const levelEl = view.querySelector<HTMLSelectElement>("#settingLevel");
  const apiEl = view.querySelector<HTMLInputElement>("#settingApiUrl");
  const modelEl = view.querySelector<HTMLInputElement>("#settingModel");
  const keyEl = view.querySelector<HTMLInputElement>("#settingApiKey");
  const autoEl = view.querySelector<HTMLInputElement>("#useAutoAssessment");
  const manualField = view.querySelector<HTMLElement>("#manualLevelField");
  const manualLevelEl = view.querySelector<HTMLSelectElement>("#manualLevel");

  if (levelEl) levelEl.value = settings.level;
  if (apiEl) apiEl.value = settings.ollamaApiUrl;
  if (modelEl) modelEl.value = settings.ollamaModel;
  if (manualLevelEl) {
    manualLevelEl.value = profile.manualOverride ?? profile.assessedLevel;
  }

  autoEl?.addEventListener("change", () => {
    if (manualField) {
      manualField.style.display = autoEl.checked ? "none" : "";
    }
  });

  view.querySelector<HTMLButtonElement>("#btnAgnesPreset")?.addEventListener("click", () => {
    if (apiEl) apiEl.value = AGNES_PRESET.ollamaApiUrl;
    if (modelEl) modelEl.value = AGNES_PRESET.ollamaModel;
    toast("已填入 Agnes 预设，请填写 API Key 后保存");
  });

  view.querySelector<HTMLButtonElement>("#btnTestConnection")?.addEventListener("click", async () => {
    const btn = view.querySelector<HTMLButtonElement>("#btnTestConnection");
    if (!btn || btn.disabled) {
      return;
    }

    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = "测试中…";

    try {
      const keyInput = keyEl?.value.trim() ?? "";
      const result = await testAiConnection({
        ollamaApiUrl: apiEl?.value.trim(),
        ollamaModel: modelEl?.value.trim(),
        apiKey: keyInput || settings.apiKey,
      });
      toast(result.message);
    } catch (err) {
      toast(err instanceof Error ? err.message : "连接失败");
    } finally {
      btn.disabled = false;
      btn.textContent = originalText ?? "测试连通性";
    }
  });

  view.querySelector<HTMLButtonElement>("#btnSaveSettings")?.addEventListener("click", async () => {
    try {
      const patch: Partial<typeof settings> = {
        level: (levelEl?.value ?? "intermediate") as EnglishLevel,
        ollamaApiUrl: apiEl?.value.trim() ?? "",
        ollamaModel: modelEl?.value.trim() ?? "",
      };
      const keyInput = keyEl?.value.trim() ?? "";
      if (keyInput) {
        patch.apiKey = keyInput;
      }
      await saveSettings(patch);

      const useAuto = autoEl?.checked ?? true;
      if (useAuto) {
        await setManualLevelOverride(null);
      } else {
        const manual = (manualLevelEl?.value ?? "intermediate") as EnglishLevel;
        await setManualLevelOverride(manual);
      }

      if (keyEl) {
        keyEl.value = "";
        keyEl.placeholder = "已设置，留空则不修改";
      }
      toast("设置已保存");
    } catch (err) {
      toast(err instanceof Error ? err.message : "保存失败");
    }
  });
}
